import React, { useEffect, useState } from 'react';
import { apiDelete, apiGet } from '../../api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const RolesList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedRoles, setSortedRoles] = useState([]);
  const navigate = useNavigate();
  const { can } = usePermissions();

  const fetchRoles = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await apiGet(`/api/admin/roles/list?page=${pageNum}&pageSize=30&order=null`, {});
      const newRoles = res.data.data.data;
      setRoles(newRoles);
      setSortedRoles(newRoles);
      setTotalPages(res.data.data.totalPages);
      setTotalRows(res.data.data.totalRows || 0);
    } catch (err) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles(page);
    // eslint-disable-next-line
  }, [page]);

  // Client-side sorting effect
  useEffect(() => {
    if (!roles.length) return;

    const sorted = [...roles].sort((a, b) => {
      if (!sort.key) return 0;

      let aValue = a[sort.key];
      let bValue = b[sort.key];

      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (sort.direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    setSortedRoles(sorted);
  }, [roles, sort]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await apiDelete(`/api/admin/roles/delete/${id}`);
      toast.success('Role deleted successfully');
      fetchRoles(page);
    } catch (err) {
      toast.error('Failed to delete role');
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Management
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">All <span className="text-gradient-ginox">Roles</span></h1>
        </div>
        {can("POST:/api/admin/roles/create") && (
          <button
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
            onClick={() => navigate('/roles/create')}
          >
            <Plus size={13} />
            Add Role
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader
                sortKey="role"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Role
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="permissions"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Permissions
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="dashboard_permissions"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Dashboard Permissions
              </SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-left">
                Actions
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : roles.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-sm text-muted-foreground">No data found</td>
              </tr>
            ) : (
              sortedRoles.map((role) => (
                <tr key={role._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-medium">
                    {role.role}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    {role.permissions && role.permissions.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.map((perm, i) => (
                          <span key={i} className="inline-block rounded px-1.5 py-0.5 font-mono text-[10px] text-ginox-green bg-ginox-green/10 border border-ginox-green/20">
                            {perm}
                          </span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    {role.dashboard_permissions && role.dashboard_permissions.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {role.dashboard_permissions.map((perm, i) => (
                          <span key={i} className="inline-block rounded px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground bg-white/[0.04] border border-white/[0.08]">
                            {perm}
                          </span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    <div className="flex items-center gap-2">
                      {can("POST:/api/admin/roles/edit/:id") && (
                        <button
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
                          onClick={() => navigate(`/roles/edit/${role._id}`)}
                        >
                          <Pencil size={12} />
                          Edit
                        </button>
                      )}
                      {can("DELETE:/api/admin/roles/delete/:id") && (
                        <button
                          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-1.5"
                          onClick={() => handleDelete(role._id)}
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalRows={totalRows}
        pageSize={30}
        onPageChange={(newPage) => setPage(newPage)}
      />
    </div>
  );
};

export default RolesList;
